import { Font, Text, View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import type { ReactElement } from 'react';

import type { Quote } from '@core/types/api/Quote/Quote';
import { randomUUID } from '@core/utils/uuid';

import { type PdfColumnConfig, PdfTable, pdfColumnFromConfig } from '../../../components/PdfTable';

type HeaderRow = {
  id: string;
  firstValue: string;
  secondValue: string;
  thirdValue: string;
  fourthValue: string;
  secondValueTextStyle?: Style;
};

const cellContainerViewStyle: Style = {
  display: 'flex',
  justifyContent: 'center',
};

const centerAlignViewStyle: Style = {
  textAlign: 'center',
};

const balthazarTextStyle: Style = {
  fontFamily: 'Balthazar',
  fontSize: '10px',
};

const sansTextStyle: Style = {
  fontFamily: 'OpenSans',
  fontSize: '10px',
};

const formatFirstValueForPdf = (value: string): ReactElement => (
  <View style={centerAlignViewStyle}>
    <Text style={balthazarTextStyle}>{value}</Text>
  </View>
);

const formatSecondValueForPdf = (value: string, row: HeaderRow): ReactElement => (
  <View style={centerAlignViewStyle}>
    <Text style={[sansTextStyle, row.secondValueTextStyle ?? {}]}>{value}</Text>
  </View>
);

const formatThirdValueForPdf = (value: string): ReactElement => (
  <View style={centerAlignViewStyle}>
    <Text style={balthazarTextStyle}>{value}</Text>
  </View>
);

const formatFourthValueForPdf = (value: string): ReactElement => (
  <View style={centerAlignViewStyle}>
    <Text style={sansTextStyle}>{value}</Text>
  </View>
);

type CityTransitMixPdfHeaderTableProps = {
  quote: Quote;
  locale: string;
};

export const CityTransitMixPdfHeaderTable = ({
  quote,
  locale,
}: CityTransitMixPdfHeaderTableProps): ReactElement => {
  Font.registerHyphenationCallback((word) => [word]);
  const rows: HeaderRow[] = [
    {
      id: randomUUID(),
      firstValue: 'Attention',
      secondValue: quote.contacts[0]?.firstName ?? ' ',
      thirdValue: 'Quote code',
      fourthValue: quote.quoteNumber,
    },
    {
      id: randomUUID(),
      firstValue: 'Company address',
      secondValue: quote.company?.name ?? ' ',
      secondValueTextStyle: { fontWeight: 'bold' },
      thirdValue: 'Phone #',
      fourthValue: quote.contacts[0]?.phoneNumber ?? ' ',
    },
    {
      id: randomUUID(),
      firstValue: ' ',
      secondValue: quote.company?.address?.addressDisplay() ?? ' ',
      thirdValue: 'Cell',
      fourthValue: ' ',
    },
    {
      id: randomUUID(),
      firstValue: 'Project',
      secondValue: quote.project.name,
      secondValueTextStyle: { fontWeight: 'bold', textDecoration: 'underline' },
      thirdValue: 'Email',
      fourthValue: quote.contacts[0]?.emailAddress ?? ' ',
    },
    {
      id: randomUUID(),
      firstValue: 'Address',
      secondValue: quote.project.address.addressDisplay(),
      thirdValue: 'Tax Exempt Status',
      fourthValue:
        quote.project.taxCode === null
          ? ' '
          : `${quote.project.taxCode.name} (${quote.project.taxCode.code})`,
    },
    {
      id: randomUUID(),
      firstValue: 'Quote Expiration Date',
      secondValue: quote.expirationDateDisplay(locale) ?? ' ',
      secondValueTextStyle: { fontWeight: 'bold' },
      thirdValue: 'Terms',
      // TODO: update when terms are tied to quotes
      fourthValue: quote.terms ?? ' ',
    },
  ];

  const headerCols: PdfColumnConfig<HeaderRow>[] = [
    {
      id: 'firstValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      widthOverride: '10%',
      headerViewStyle: cellContainerViewStyle,
      rowViewStyle: cellContainerViewStyle,
      formatValueForPdf: formatFirstValueForPdf,
    },
    {
      id: 'secondValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      widthOverride: '40%',
      headerViewStyle: cellContainerViewStyle,
      rowViewStyle: cellContainerViewStyle,
      formatValueForPdf: formatSecondValueForPdf,
    },
    {
      id: 'thirdValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      widthOverride: '20%',
      headerViewStyle: cellContainerViewStyle,
      rowViewStyle: cellContainerViewStyle,
      formatValueForPdf: formatThirdValueForPdf,
    },
    {
      id: 'fourthValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      widthOverride: '30%',
      headerViewStyle: cellContainerViewStyle,
      rowViewStyle: cellContainerViewStyle,
      formatValueForPdf: formatFourthValueForPdf,
    },
  ];

  return (
    <PdfTable
      columns={headerCols.map(pdfColumnFromConfig)}
      rows={rows}
      cellPadding='tight'
      bordered
    />
  );
};
